import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import './LottoDraw.scss';
import { RootState } from '../../configs/store';
import LottoContext from 'src/components/NumbersBetting/Context/Lotto.context';
import Page from '../../containers/Page/Page';
import DetailHeader from '../../components/DetailHeader/DetailHeader';
import DrawCalendar from '../../components/NumbersBetting/DrawCalendar/DrawCalendar';
import LottoMarket from '../../components/NumbersBetting/LottoMarket/LottoMarket';
import SimpleTabs from '../../components/SimpleTabs/SimpleTabs';
import LottoNumberPicker from 'src/components/NumbersBetting/LottoNumberPicker/LottoNumberPicker';
import LottoSummary from 'src/components/NumbersBetting/LottoSummary/LottoSummary';
import QuickBet from '../../components/Betslip/QuickBet/QuickBet';
import DrawHistory from 'src/components/NumbersBetting/DrawHistory/DrawHistory';
import SearchResults from '../../components/SearchResults/SearchResults';
import Summary from 'src/components/Summary/Summary';
import useIsMobile from 'src/hooks/useIsMobile';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import FastMode from 'src/components/HorseBetting/FastMode/FastMode';

export default function LottoDraw() {
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedSpecialEvents, setSelectedSpecialEvents] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedNumbers, setselectedNumbers] = useState([]);
  const [usedNumbers, setUsedNumbers] = useState([]);
  const [combinations, setCombinations] = useState();
  const [quickBet, setQuickBet] = useState(false);
  const [tab, setTab] = useState(null);
  const [retailStake, setRetailStake] = useState(10);
  const [selectedNumbersForEachMarket, setNumbersForEachMarket] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isManualSelection, setIsManualSelection] = useState(false);

  const { RETAIL_MODE } = useContext(AppModeContext);

  const { tournamentCode, eventId } = useParams<{ tournamentCode: string, eventId: string }>();
  const { t } = useTranslation('common');
  const mobile = useIsMobile();

  const dispatch = useDispatch();
  const {
    singleNumberEvent: { countryCode, name, marketType, couponCode, cardCode, numberInfos: { description, imageDesktop }, country }
  }  = useSelector((state: RootState) => state.lotto);
  const { isSearching, isDirty } = useSelector((state: RootState) => state.events);

  const context = {
    selectedEvents,
    selectedMarket,
    selectedNumbers,
    tab,
    combinations,
    quickBet,
    usedNumbers,
    retailStake,
    selectedNumbersForEachMarket,
    selectedSpecialEvents,
    isLoading,
    isManualSelection,
    setUsedNumbers,
    setSelectedEvents,
    setSelectedMarket,
    setselectedNumbers,
    setTab,
    setCombinations,
    setQuickBet,
    setRetailStake,
    setNumbersForEachMarket,
    setSelectedSpecialEvents,
    setIsLoading,
    setIsManualSelection
  };

  useEffect(() => {
    let dirty = false;
    //Check if selected events are different from default selected
    if (selectedEvents.length > 1){
      const events = _.filter(selectedEvents, { selected: true });
      if (events.length > 1){
        dirty = true;
      } else if (events[0]?.eventCode !== selectedEvents[0].eventCode){
        dirty = true;
      }
    }
    const alredyHaveSelections = _.filter(selectedNumbersForEachMarket, (selections) => {
      return !_.isEmpty(selections);
    }).length > 0;

    // Check if selected numbers should update dirty state
    if (alredyHaveSelections || selectedNumbers?.length > 0){
      dirty= true;
    }

    //  Check if selected Markets are different from default selected
    if (marketType?.length > 1){
      if (selectedMarket && selectedMarket?.typeCode !== marketType[0].typeCode){
        dirty = true;
      }
    }

    if (isDirty !== dirty){
      dispatch.events.changeDirtiness(dirty);
    }
  }, [selectedEvents, selectedNumbers, selectedMarket]);

  useEffect(() => {
    const loadEvent = async (shouldReset = false) => {
      setIsLoading(shouldReset);
      await dispatch.lotto.getNumberEventByCode({ hideLoading: true, tournamentCode, eventId });
      setIsLoading(false);
    };
    loadEvent();
  }, [eventId, tournamentCode]);

  useEffect(() => {
    document.title = `BetXchange - Lotto ~ ${ name ?? '' }`;

    return (() => {
      setSelectedEvents([]);
    });
  }, [name]);

  return (
    <Page>
      {
        isSearching && !mobile
          ? <SearchResults />
          : (
            <LottoContext.Provider value={ context }>
              {
                RETAIL_MODE && <FastMode>
                  <FastMode.Numbers card={ cardCode } coupon={ couponCode } selectedEvents={ _.filter(selectedEvents, { selected: true }) } />
                </FastMode>
              }
              <div className="lotto-draw__wrapper">
                <DetailHeader>
                  <DetailHeader.LottoDraw event={ { country, countryCode, name, description, imageDesktop, couponCode, cardCode } } />
                </DetailHeader>
                <SimpleTabs
                  labels={
                    [
                      { label: t('numbers.draws') },
                      { label: t('numbers.drawHistory') }
                    ]
                  }
                  containerClass="lotto-draw"
                  showIndicator={ false }
                >
                  <>
                    <DrawCalendar />
                    <LottoMarket />
                    {
                      tab < 801 && tab && (
                        <div>
                          <LottoNumberPicker />
                          {
                            selectedMarket && selectedMarket.specifiers &&
                            <Summary>
                              <LottoSummary />
                            </Summary>
                          }
                        </div>
                      )
                    }
                  </>
                  <DrawHistory />
                </SimpleTabs>
                {
                  quickBet && (
                    <QuickBet tournamentName={ name } country={ country } />
                  )
                }
              </div>
            </LottoContext.Provider>
          )
      }
    </Page>
  );
}
